import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import clientService from '../../../services/clientService';
import NotificationToast from '../../../components/ui/NotificationToast';

const CreateInvoiceModal = ({ isOpen, onClose, onSave, invoice = null, isEditMode = false }) => {
  const [formData, setFormData] = useState({
    clientId: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    services: [{ description: '', sacCode: '', quantity: 1, rate: 0, amount: 0 }],
    gstRate: 18,
    notes: '',
    paymentTerms: '30'
  });

  const [errors, setErrors] = useState({});
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [notification, setNotification] = useState(null);
  const formPopulatedRef = useRef(false);

  // Load clients when modal opens
  useEffect(() => {
    if (isOpen) {
      loadClients();
    }
  }, [isOpen]);

  // Populate form if editing
  useEffect(() => {
    // Reset form populated flag when modal closes
    if (!isOpen) {
      formPopulatedRef.current = false;
      return;
    }
    
    // Only populate once when modal opens in edit mode
    if (isOpen && isEditMode && invoice && clients.length > 0 && !formPopulatedRef.current) {
      formPopulatedRef.current = true;
      // Find client by name or email
      const client = clients.find(c => 
        c.company_name === invoice.clientName || 
        c.email === invoice.clientEmail
      );
      
      // Calculate GST rate from amounts if available
      let gstRate = 18;
      if (invoice.totalAmount && invoice.amount) {
        const gstAmount = invoice.totalAmount - invoice.amount;
        if (invoice.amount > 0) {
          gstRate = Math.round((gstAmount / invoice.amount) * 100);
        }
      }
      
      // Reconstruct services array from invoice data
      let services = [];
      
      console.log('🔍 Editing invoice - services data:', {
        hasServices: !!invoice.services,
        servicesType: typeof invoice.services,
        servicesIsArray: Array.isArray(invoice.services),
        servicesValue: invoice.services
      });
      
      // Try to parse services from various formats
      if (invoice.services) {
        if (Array.isArray(invoice.services) && invoice.services.length > 0) {
          // Already an array
          console.log('✅ Services is array, mapping:', invoice.services.length, 'items');
          services = invoice.services.map(s => ({
            description: s.description || '',
            sacCode: s.sacCode || s.sac_code || '',
            quantity: parseFloat(s.quantity) || 1,
            rate: parseFloat(s.rate) || 0,
            amount: parseFloat(s.amount) || (parseFloat(s.rate) || 0) * (parseFloat(s.quantity) || 1)
          }));
        } else if (typeof invoice.services === 'string') {
          // Try to parse JSON string
          try {
            const parsed = JSON.parse(invoice.services);
            console.log('✅ Parsed services JSON:', parsed);
            if (Array.isArray(parsed) && parsed.length > 0) {
              services = parsed.map(s => ({
                description: s.description || '',
                sacCode: s.sacCode || s.sac_code || '',
                quantity: parseFloat(s.quantity) || 1,
                rate: parseFloat(s.rate) || 0,
                amount: parseFloat(s.amount) || (parseFloat(s.rate) || 0) * (parseFloat(s.quantity) || 1)
              }));
            }
          } catch (e) {
            console.warn('❌ Failed to parse services JSON in edit form:', e);
          }
        }
      }
      
      // If no services found, create default from invoice data
      if (services.length === 0) {
        console.log('⚠️ No services found, creating default');
        const description = invoice.description || 'Service';
        const sacCode = invoice.sacCode || invoice.sac_code || '';
        const amount = parseFloat(invoice.amount || 0);
        
        services = [{
          description: description,
          sacCode: sacCode,
          quantity: 1,
          rate: amount,
          amount: amount
        }];
      }
      
      console.log('📋 Final services for form:', services);
      console.log('📋 Services count:', services.length);
      console.log('📋 Services details:', services.map((s, i) => ({
        index: i,
        description: s.description,
        sacCode: s.sacCode,
        quantity: s.quantity,
        rate: s.rate,
        amount: s.amount
      })));
      
      // Ensure services is an array with at least one item
      const finalServices = Array.isArray(services) && services.length > 0 
        ? services 
        : [{
            description: invoice.description || 'Service',
            sacCode: invoice.sacCode || invoice.sac_code || '',
            quantity: 1,
            rate: parseFloat(invoice.amount || 0),
            amount: parseFloat(invoice.amount || 0)
          }];
      
      console.log('📋 Setting formData with services:', finalServices.length, 'items');
      
      setFormData({
        clientId: client?.id || '',
        invoiceDate: invoice.invoiceDate || invoice.issue_date || new Date().toISOString().split('T')[0],
        dueDate: invoice.dueDate || invoice.due_date || '',
        gstRate: invoice.gstRate || gstRate,
        notes: invoice.description || '',
        paymentTerms: '30',
        services: finalServices
      });
    } else if (isOpen && !isEditMode) {
      // Reset form for new invoice
      setFormData({
        clientId: '',
        invoiceDate: new Date().toISOString().split('T')[0],
        dueDate: '',
        services: [{ description: '', sacCode: '', quantity: 1, rate: 0, amount: 0 }],
        gstRate: 18,
        notes: '',
        paymentTerms: '30'
      });
    }
  }, [isOpen, isEditMode, invoice?.id]); // Only depend on invoice ID to prevent re-running

  const loadClients = async () => {
    try {
      setLoadingClients(true);
      const result = await clientService.getClients({ status: 'active' });
      
      if (result.success && result.data) {
        setClients(result.data);
      } else {
        setNotification({ 
          message: result.error || 'Failed to load clients', 
          type: 'error' 
        });
      }
    } catch (error) {
      console.error('Error loading clients:', error);
      setNotification({ 
        message: 'Failed to load clients', 
        type: 'error' 
      });
    } finally {
      setLoadingClients(false);
    }
  };

  const sacCodes = [
    { code: '998314', description: 'Facility Management Services' },
    { code: '998313', description: 'Security Services' },
    { code: '998311', description: 'Cleaning Services' },
    { code: '998312', description: 'Maintenance Services' },
    { code: '998315', description: 'Housekeeping Services' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // If client is selected, we can optionally auto-populate some fields
    if (field === 'clientId' && value) {
      const selectedClient = clients.find(c => c.id === value);
      if (selectedClient) {
        // Client details are now available for invoice creation
        // We'll use them when submitting
      }
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleServiceChange = (index, field, value) => {
    const updatedServices = [...formData.services];
    updatedServices[index] = {
      ...updatedServices[index],
      [field]: value
    };

    // Calculate amount for this service
    if (field === 'quantity' || field === 'rate') {
      const quantity = field === 'quantity' ? parseFloat(value) || 0 : updatedServices[index].quantity;
      const rate = field === 'rate' ? parseFloat(value) || 0 : updatedServices[index].rate;
      updatedServices[index].amount = quantity * rate;
    }

    setFormData(prev => ({
      ...prev,
      services: updatedServices
    }));
  };

  const addService = () => {
    setFormData(prev => ({
      ...prev,
      services: [...prev.services, { description: '', sacCode: '', quantity: 1, rate: 0, amount: 0 }]
    }));
  };

  const removeService = (index) => {
    if (formData.services.length > 1) {
      setFormData(prev => ({
        ...prev,
        services: prev.services.filter((_, i) => i !== index)
      }));
    }
  };

  const calculateTotals = () => {
    const subtotal = formData.services.reduce((sum, service) => sum + service.amount, 0);
    const gstAmount = (subtotal * formData.gstRate) / 100;
    const total = subtotal + gstAmount;
    
    return { subtotal, gstAmount, total };
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.clientId) newErrors.clientId = 'Please select a client';
    if (!formData.dueDate) newErrors.dueDate = 'Due date is required';
    
    formData.services.forEach((service, index) => {
      if (!service.description) newErrors[`service_${index}_description`] = 'Description is required';
      if (!service.sacCode) newErrors[`service_${index}_sacCode`] = 'SAC code is required';
      if (service.rate <= 0) newErrors[`service_${index}_rate`] = 'Rate must be greater than 0';
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    const { subtotal, gstAmount, total } = calculateTotals();
    const selectedClient = clients.find(c => c.id === formData.clientId);
    
    if (!selectedClient) {
      setErrors(prev => ({ ...prev, clientId: 'Please select a valid client' }));
      setNotification({ 
        message: 'Please select a client before creating the invoice', 
        type: 'error' 
      });
      return;
    }
    
    // Validate client has company_name
    const companyName = selectedClient.company_name || selectedClient.name || '';
    if (!companyName || companyName.trim() === '') {
      setErrors(prev => ({ ...prev, clientId: 'Selected client does not have a company name' }));
      setNotification({ 
        message: 'Selected client is missing company name. Please select a different client or contact administrator.', 
        type: 'error' 
      });
      return;
    }
    
    // Validate required dates
    if (!formData.dueDate) {
      setErrors(prev => ({ ...prev, dueDate: 'Due date is required' }));
      setNotification({ 
        message: 'Please select a due date', 
        type: 'error' 
      });
      return;
    }
    
    const invoiceData = {
      client_name: companyName.trim(),
      client_email: selectedClient.email || selectedClient.contact_email || '',
      amount: subtotal,
      tax_amount: gstAmount,
      total_amount: total,
      issue_date: formData.invoiceDate,
      due_date: formData.dueDate,
      status: isEditMode ? (invoice?.status || 'draft') : 'draft',
      description: formData.notes || '',
      // Store services as JSON string for retrieval during edit
      // Ensure all services have proper structure
      services: JSON.stringify(formData.services.map(s => ({
        description: s.description || '',
        sacCode: s.sacCode || '',
        quantity: parseFloat(s.quantity) || 1,
        rate: parseFloat(s.rate) || 0,
        amount: parseFloat(s.amount) || (parseFloat(s.rate) || 0) * (parseFloat(s.quantity) || 1)
      }))),
      gstRate: formData.gstRate,
      paymentTerms: formData.paymentTerms
    };

    // Debug log
    console.log('📝 Invoice data being sent:', {
      client_name: invoiceData.client_name,
      client_email: invoiceData.client_email,
      amount: invoiceData.amount,
      total_amount: invoiceData.total_amount,
      due_date: invoiceData.due_date
    });

    onSave(invoiceData);
    onClose();
    
    // Reset form
    setFormData({
      clientId: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      dueDate: '',
      services: [{ description: '', sacCode: '', quantity: 1, rate: 0, amount: 0 }],
      gstRate: 18,
      notes: '',
      paymentTerms: '30'
    });
    setErrors({});
  };

  const { subtotal, gstAmount, total } = calculateTotals();

  if (!isOpen) return null;

  return (
    <>
      {notification && (
        <NotificationToast
          message={notification.message}
          type={notification.type}
          onDismiss={() => setNotification(null)}
          position="top-right"
          autoHideDuration={3000}
        />
      )}
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                {isEditMode ? 'Edit Invoice' : 'Create New Invoice'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {isEditMode ? 'Update invoice details' : 'Generate GST compliant invoice'}
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Client and Date Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Select Client *
                </label>
                {loadingClients ? (
                  <div className="w-full px-3 py-2 border border-border rounded-lg bg-background flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                    <span className="text-sm text-muted-foreground">Loading clients...</span>
                  </div>
                ) : (
                  <select
                    value={formData.clientId}
                    onChange={(e) => handleInputChange('clientId', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring ${
                      errors.clientId ? 'border-error' : 'border-border'
                    }`}
                  >
                    <option value="">Choose a client...</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.company_name} {client.contact_person ? `(${client.contact_person})` : ''}
                      </option>
                    ))}
                  </select>
                )}
                {errors.clientId && (
                  <p className="text-sm text-error mt-1">{errors.clientId}</p>
                )}
                {!loadingClients && clients.length === 0 && (
                  <p className="text-xs text-muted-foreground mt-1">No active clients found</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="date"
                  label="Invoice Date"
                  value={formData.invoiceDate}
                  onChange={(e) => handleInputChange('invoiceDate', e.target.value)}
                />
                <Input
                  type="date"
                  label="Due Date *"
                  value={formData.dueDate}
                  onChange={(e) => handleInputChange('dueDate', e.target.value)}
                  error={errors.dueDate}
                />
              </div>
            </div>

            {/* Services Section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-foreground">Services</h3>
                <Button type="button" variant="outline" size="sm" onClick={addService}>
                  <Icon name="Plus" size={16} className="mr-1" />
                  Add Service
                </Button>
              </div>

              <div className="space-y-4">
                {formData.services && Array.isArray(formData.services) && formData.services.length > 0 ? (
                  formData.services.map((service, index) => (
                    <div key={`service-${index}-${service.description || ''}-${service.sacCode || ''}`} className="border border-border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-foreground">Service {index + 1}</h4>
                      {formData.services.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeService(index)}
                          className="text-error hover:text-error"
                        >
                          <Icon name="Trash2" size={16} />
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                      <div className="lg:col-span-2">
                        <Input
                          label="Service Description *"
                          value={service.description}
                          onChange={(e) => handleServiceChange(index, 'description', e.target.value)}
                          placeholder="Describe the service..."
                          error={errors[`service_${index}_description`]}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          SAC Code *
                        </label>
                        <select
                          value={service.sacCode}
                          onChange={(e) => handleServiceChange(index, 'sacCode', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring ${
                            errors[`service_${index}_sacCode`] ? 'border-error' : 'border-border'
                          }`}
                        >
                          <option value="">Select SAC...</option>
                          {sacCodes.map(sac => (
                            <option key={sac.code} value={sac.code}>
                              {sac.code} - {sac.description}
                            </option>
                          ))}
                        </select>
                        {errors[`service_${index}_sacCode`] && (
                          <p className="text-sm text-error mt-1">{errors[`service_${index}_sacCode`]}</p>
                        )}
                      </div>

                      <div>
                        <Input
                          type="number"
                          label="Quantity"
                          value={service.quantity}
                          onChange={(e) => handleServiceChange(index, 'quantity', e.target.value)}
                          min="1"
                          step="1"
                        />
                      </div>

                      <div>
                        <Input
                          type="number"
                          label="Rate (₹) *"
                          value={service.rate}
                          onChange={(e) => handleServiceChange(index, 'rate', e.target.value)}
                          min="0"
                          step="0.01"
                          error={errors[`service_${index}_rate`]}
                        />
                      </div>
                    </div>

                    <div className="mt-4 p-3 bg-muted/30 rounded-lg">
                      <div className="text-right">
                        <span className="text-sm text-muted-foreground">Amount: </span>
                        <span className="font-medium text-foreground">
                          ₹{service.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                  </div>
                  ))
                ) : (
                  <div className="border border-border rounded-lg p-4 text-center text-muted-foreground">
                    No services added. Click "Add Service" to add one.
                  </div>
                )}
              </div>
            </div>

            {/* Tax and Payment Terms */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Input
                  type="number"
                  label="GST Rate (%)"
                  value={formData.gstRate}
                  onChange={(e) => handleInputChange('gstRate', e.target.value)}
                  min="0"
                  max="28"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Payment Terms
                </label>
                <select
                  value={formData.paymentTerms}
                  onChange={(e) => handleInputChange('paymentTerms', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="15">Net 15 days</option>
                  <option value="30">Net 30 days</option>
                  <option value="45">Net 45 days</option>
                  <option value="60">Net 60 days</option>
                </select>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Additional Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Any additional terms or notes..."
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              />
            </div>

            {/* Invoice Summary */}
            <div className="bg-muted/30 rounded-lg p-6">
              <h3 className="text-lg font-medium text-foreground mb-4">Invoice Summary</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal:</span>
                  <span className="font-medium text-foreground">
                    ₹{subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">GST ({formData.gstRate}%):</span>
                  <span className="font-medium text-foreground">
                    ₹{gstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="border-t border-border pt-2">
                  <div className="flex justify-between">
                    <span className="text-lg font-medium text-foreground">Total Amount:</span>
                    <span className="text-lg font-bold text-primary">
                      ₹{total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border">
          <div className="flex items-center justify-end gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              <Icon name={isEditMode ? "Save" : "FileText"} size={16} className="mr-2" />
              {isEditMode ? 'Update Invoice' : 'Create Invoice'}
            </Button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default CreateInvoiceModal;